import { __read, __spreadArray } from "tslib";
import { ActionEntityType, ActionIntent, DefaultOfficeAppId, OfficeActionId, } from '@office-start/action-types';
import { ActionContextMenuItemIcon, ContentActionGroup, FileType, } from '@office-start/document-types';
import { getFileLocationUrl, getReferralFromFileLocation, getReferralFromFileType, hasDownloadCapability, } from '@office-start/file-utility';
import { getNativeUrl } from '@office-start/launcher-service';
import { ShareControlMode } from '@office-start/share-types';
import { OfficeActionCreators } from '../actionCreators/officeActionCreators';
import { logRegisteredActions } from '../actionTelemetry';
import { getAddToToDoActions, getConvertToPdfActions } from '../util';
import { getDeleteActions } from './getDeleteAction';
var getShareAction = function (companyDisplayName, content, mode, targetUX, disabled, tooltipString, shareUIPositionOverride) {
    var _a, _b;
    // A valid type must be passed to the OfficeAction creator
    // Make sure here that type is valid before continuing
    if (!((_b = (_a = content.actionInfo) === null || _a === void 0 ? void 0 : _a.odspShareInfo) === null || _b === void 0 ? void 0 : _b.enabled) ||
        content.actionInfo.odspShareInfo.type === undefined) {
        return undefined;
    }
    return OfficeActionCreators.createShareOrCopyAction(companyDisplayName, content.fileName || '', content.actionInfo.odspShareInfo, mode, targetUX === null || targetUX === void 0 ? void 0 : targetUX.targetElementId, targetUX === null || targetUX === void 0 ? void 0 : targetUX.style, content.fileType, tooltipString, {
        disabled: disabled,
    }, shareUIPositionOverride);
};
/**
 * @description Register hub actions like (open in browser/desktop, share, copy link, download, open file and open in app)
 * @param strings Localized strings to populate the system actions
 * @param appId Office hub app id (could be aadv2 app id from context, could be a guid, but should always be the same)
 */
export var registerHubActions = function (actionManager, systemUserConfig, resourceStrings, appId) {
    if (appId === void 0) { appId = DefaultOfficeAppId; }
    var documentActionsMenuResources = resourceStrings.documentActionsMenuResources, documentActionsToastResources = resourceStrings.documentActionsToastResources, actionsInContextDialogResources = resourceStrings.actionsInContextDialogResources;
    var copyLinkActionTitle = documentActionsMenuResources.copyLinkActionTitle, downloadActionTitle = documentActionsMenuResources.downloadActionTitle, openFileLocationActionTitleSentence = documentActionsMenuResources.openFileLocationActionTitleSentence, openMenuTitle = documentActionsMenuResources.openMenuTitle, openInAppActionTitle = documentActionsMenuResources.openInAppActionTitle, openInBrowserActionTitleSentence = documentActionsMenuResources.openInBrowserActionTitleSentence, openInDesktopActionTitleSentence = documentActionsMenuResources.openInDesktopActionTitleSentence, scheduleActionTitle = documentActionsMenuResources.scheduleActionTitle, shareToEmailActionTitle = documentActionsMenuResources.shareToEmailActionTitle, shareToTeamsActionTitle = documentActionsMenuResources.shareToTeamsActionTitle, disabledDownloadActionTooltip = documentActionsMenuResources.disabledDownloadActionTooltip, disabledShareActionTooltip = documentActionsMenuResources.disabledShareActionTooltip, disabledOpenFileLocationActionTooltip = documentActionsMenuResources.disabledOpenFileLocationActionTooltip;
    var _a = systemUserConfig.deviceInfo, isPlatformMobileOrTablet = _a.isPlatformMobileOrTablet, isHwa = _a.isHwa, isEmbed = _a.isEmbed, browserType = _a.browserType;
    var exchangeEnabled = systemUserConfig.exchangeEnabled, featureGates = systemUserConfig.featureGates, oneDriveEnabled = systemUserConfig.oneDriveEnabled, userType = systemUserConfig.userType;
    var isConsumerGraphUser = userType === 'Consumer' && featureGates.graphApiForV2ConsumerEnabled;
    // Notification options for local copy link action
    //example
    var localCopyLinkNotificationOptions = {
        error: {
            message: documentActionsToastResources.copyLinkFailed,
            actionText: documentActionsToastResources.closeButtonText,
        },
        loading: {
            showLoadingIndicator: true,
        },
        success: {
            message: documentActionsToastResources.linkCopied,
            actionText: documentActionsToastResources.openButtonText,
        },
    };
    // Add in supported actions per configuration (settings/device type/license, etc)
    var actions = [
        // When in Browser : Open content inline or in new browser (default click on document / open action)
        // Icon - OpenIcon
        // Sub-menu will have Open in desktop action
        // When in Desktop : Open in respective desktop app if supported else open in browser
        // Icon - Content fileType brand icon
        // Sub-menu will have Open in Browser action
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.OpenMenu,
                name: openMenuTitle,
                description: openMenuTitle,
                id: OfficeActionId.Open,
                icon: isHwa
                    ? {
                        type: ActionContextMenuItemIcon.Brand,
                        fileType: FileType.None,
                    }
                    : {
                        type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                        iconName: 'OpenIcon',
                    },
            },
            officeActionCreator: function (content) {
                var options = {
                    referral: getReferralFromFileType(content.fileType),
                    referralPlatform: isHwa ? 'Native' : 'Web',
                };
                if (featureGates.linksEnabled &&
                    content.fileType === FileType.Link &&
                    content.driveId &&
                    content.docId) {
                    options.notificationOptions = {
                        loading: {
                            showLoadingIndicator: false,
                        },
                        error: {
                            message: 'Failed to open link.',
                        },
                    };
                    return OfficeActionCreators.createLinkContentOpenAction(content, options);
                }
                return OfficeActionCreators.createContentOpenAction(content, options);
            },
        },
        isHwa
            ? // Open content in browser in new tab
                {
                    intent: ActionIntent.ContentView,
                    entities: [{ type: ActionEntityType.Content }],
                    metadata: {
                        appId: appId,
                        groupId: ContentActionGroup.Open,
                        name: openInBrowserActionTitleSentence,
                        description: openInBrowserActionTitleSentence,
                        id: OfficeActionId.OpenInBrowser,
                        icon: {
                            type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                            iconName: 'OpenIcon',
                        },
                    },
                    officeActionCreator: function (content) {
                        var _a;
                        if (!content.webUrl) {
                            return undefined;
                        }
                        return OfficeActionCreators.createContentUrlAction(content.webUrl, (_a = content.isEmailAttachment) !== null && _a !== void 0 ? _a : false, '_blank', {
                            referral: getReferralFromFileType(content.fileType),
                            referralPlatform: 'Web',
                        });
                    },
                }
            : // Open in native app (from mobile)
                {
                    intent: ActionIntent.ContentView,
                    entities: [{ type: ActionEntityType.Content }],
                    metadata: {
                        appId: appId,
                        groupId: ContentActionGroup.Open,
                        // This is a format string, consumers should replace {0} with the name of the app!
                        name: isPlatformMobileOrTablet
                            ? openInAppActionTitle
                            : openInDesktopActionTitleSentence,
                        description: isPlatformMobileOrTablet
                            ? openInAppActionTitle
                            : openInDesktopActionTitleSentence,
                        id: OfficeActionId.OpenInApp,
                        icon: {
                            type: ActionContextMenuItemIcon.Brand,
                            fileType: FileType.None,
                        },
                    },
                    officeActionCreator: function (content) {
                        // Use URL for proxy to determine if user has redeemed/opened this file
                        // If no URL, opening in native app would fail.
                        if (!content.url) {
                            return undefined;
                        }
                        var nativeUrl = getNativeUrl(content);
                        if (!nativeUrl) {
                            return undefined;
                        }
                        return OfficeActionCreators.createContentNativeDownloadAction(nativeUrl, '_blank', {
                            referral: getReferralFromFileType(content.fileType),
                            referralPlatform: 'Native',
                        });
                    },
                },
        // Open file location
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.OpenHero,
                name: openFileLocationActionTitleSentence,
                description: openFileLocationActionTitleSentence,
                id: OfficeActionId.OpenFileLocation,
                icon: {
                    type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                    iconName: 'FolderIcon',
                },
            },
            officeActionCreator: function (content) {
                var _a, _b;
                if (!content.attributionUrl) {
                    return undefined;
                }
                // If attribution URL is relative, or not a real URL, return undefined
                try {
                    new URL(content.attributionUrl);
                }
                catch (_c) {
                    return undefined;
                }
                var referral = content.webUrl
                    ? getReferralFromFileLocation(content.webUrl)
                    : undefined;
                var referralPlatform = content.isLocalFile ? 'Native' : 'Web';
                if (content.isLocalFile) {
                    return OfficeActionCreators.createContentLocationLocalAction(content.attributionUrl, {
                        referral: referral,
                        referralPlatform: referralPlatform,
                    });
                }
                var isDisabled = ((_b = (_a = content.actionInfo) === null || _a === void 0 ? void 0 : _a.openFileLocationActionInfo) === null || _b === void 0 ? void 0 : _b.disabled) ||
                    false;
                return OfficeActionCreators.createContentLocationAction(content.attributionUrl, '_blank', isDisabled
                    ? disabledOpenFileLocationActionTooltip
                    : undefined, {
                    referral: referral,
                    referralPlatform: referralPlatform,
                    disabled: isDisabled,
                });
            },
        },
        // Copy local file path to clipboard
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.Share,
                name: copyLinkActionTitle,
                description: copyLinkActionTitle,
                id: OfficeActionId.LocalCopyLink,
                icon: {
                    type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                    iconName: 'LinkIcon',
                },
                notificationOptions: localCopyLinkNotificationOptions,
            },
            officeActionCreator: function (content) {
                if (!content.isLocalFile) {
                    return undefined;
                }
                var fileLocationUrl = getFileLocationUrl(content);
                if (!fileLocationUrl) {
                    return undefined;
                }
                return OfficeActionCreators.createCopyToClipboardAction(fileLocationUrl, {
                    notificationOptions: localCopyLinkNotificationOptions,
                });
            },
        },
    ];
    // Download is currently gated on browser, and disabled in Edge
    if (featureGates.downloadActionEnabled) {
        actions.push({
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.None,
                name: downloadActionTitle,
                description: downloadActionTitle,
                id: OfficeActionId.Download,
                icon: {
                    type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                    iconName: 'DownloadIcon',
                },
            },
            officeActionCreator: function (content) {
                var _a, _b;
                if (
                // Download URL is mostly the same as URL (without any potential query param additions)
                // downloadUrl is used today as a proxy for access and redemption.
                !content.downloadUrl ||
                    content.downloadUrl == content.webUrl ||
                    // Email attachments can have a downloadUrl. It is populated with an endpoint
                    // that we do not currently support (no auth)
                    content.isEmailAttachment ||
                    content.isLocalFile ||
                    !content.driveId ||
                    !content.docId) {
                    return undefined;
                }
                var isDisabled = ((_b = (_a = content.actionInfo) === null || _a === void 0 ? void 0 : _a.downloadActionInfo) === null || _b === void 0 ? void 0 : _b.disabled) || false;
                // For default content Actions, when download is not supported, download is not displayed.
                // In the case where isDisabled is set to true, download is displayed but disabled
                if (!isDisabled && !hasDownloadCapability(content)) {
                    return undefined;
                }
                return OfficeActionCreators.createContentDownloadAction(content.downloadUrl, content.driveId, content.docId, content.sharepointIds, '_blank', isDisabled ? disabledDownloadActionTooltip : undefined, {
                    disabled: isDisabled,
                });
            },
        });
    }
    if (featureGates.odspShareLinkEnabled) {
        actions.push(
        // Share via Share Control
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.Share,
                name: shareToEmailActionTitle,
                description: shareToEmailActionTitle,
                id: OfficeActionId.Share,
                icon: {
                    type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                    iconName: 'MailIcon',
                },
            },
            officeActionCreator: function (content, targetUX, shareUIPositionOverride) {
                var _a, _b;
                var isDisabled = ((_b = (_a = content.actionInfo) === null || _a === void 0 ? void 0 : _a.shareActionInfo) === null || _b === void 0 ? void 0 : _b.disabled) || false;
                return getShareAction(systemUserConfig.companyDisplayName, content, ShareControlMode.Share, targetUX, isDisabled, isDisabled ? disabledShareActionTooltip : undefined, shareUIPositionOverride);
            },
        });
    }
    // Add delete and next actions for delete
    if (featureGates.deleteActionEnabled) {
        actions.push.apply(actions, __spreadArray([], __read(getDeleteActions({
            appId: appId,
            hubActionStrings: resourceStrings,
            systemUserConfig: systemUserConfig,
        })), false));
    }
    if (featureGates.odspCopyLinkEnabled) {
        actions.push(
        // Copy via Share Control
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.Share,
                name: copyLinkActionTitle,
                description: copyLinkActionTitle,
                id: OfficeActionId.CopyLink,
                icon: {
                    type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                    iconName: 'LinkIcon',
                },
            },
            officeActionCreator: function (content, targetUX, shareUIPositionOverride) {
                // If local file, we want the local copy link behavior.
                if (content.isLocalFile) {
                    return undefined;
                }
                return getShareAction(systemUserConfig.companyDisplayName, content, ShareControlMode.CopyLink, targetUX, undefined, undefined, shareUIPositionOverride);
            },
        });
    }
    // Share To Teams: Currently Share to teams endpoint api is enabled only for AAD users,
    // works for Chrome & Edge browsers and works with cookieAuth
    if (featureGates.shareToTeamsEnabled &&
        userType == 'Enterprise' &&
        !systemUserConfig.isGovernment &&
        (isHwa ||
            browserType == "Chrome" /* BrowserType.Chrome */ ||
            browserType == "Edge" /* BrowserType.Edge */ ||
            browserType == "EdgeLegacy" /* BrowserType.EdgeLegacy */)) {
        actions.push(
        // Share To Teams
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.Share,
                name: shareToTeamsActionTitle,
                description: shareToTeamsActionTitle,
                id: OfficeActionId.ShareToTeams,
                icon: {
                    type: ActionContextMenuItemIcon.Svg,
                    iconName: 'TeamsSvgIcon',
                },
            },
            officeActionCreator: function (content) {
                if (!content.webUrl || content.isLocalFile) {
                    return undefined;
                }
                var driveId = content.driveId, itemId = content.docId, webUrl = content.webUrl, isSharePointFile = content.isSharePointFile, sharePointIds = content.sharepointIds, isRedeemed = content.isRedeemed;
                return OfficeActionCreators.createContentShareToTeamsAction(webUrl, shareToTeamsActionTitle, actionsInContextDialogResources, true /* shouldOpenExternal. Do not open iframe with in-context dialog due to a11y issues */, featureGates.createSharingLinkApiEnabled, systemUserConfig.companyDisplayName, driveId, itemId, isSharePointFile ? sharePointIds === null || sharePointIds === void 0 ? void 0 : sharePointIds.siteId : undefined, isRedeemed);
            },
        });
    }
    if (featureGates.scheduleEnabled &&
        (userType == 'Consumer' || systemUserConfig.calendarEnabled)) {
        actions.push(
        // Schedule
        {
            intent: ActionIntent.ContentView,
            entities: [{ type: ActionEntityType.Content }],
            metadata: {
                appId: appId,
                groupId: ContentActionGroup.AddTo,
                name: scheduleActionTitle,
                description: scheduleActionTitle,
                id: OfficeActionId.Schedule,
                icon: {
                    type: ActionContextMenuItemIcon.Svg,
                    iconName: 'OutlookSvgIcon',
                },
                notificationOptions: {
                    loading: {
                        showLoadingIndicator: false,
                    },
                },
            },
            officeActionCreator: function (content) {
                if (!(content.title || content.fileName) ||
                    !content.webUrl) {
                    return undefined;
                }
                if (content.fileType === FileType.MeetingObject) {
                    return undefined;
                }
                var fileName = content.fileName, title = content.title, webUrl = content.webUrl, isRedeemed = content.isRedeemed;
                return OfficeActionCreators.createScheduleAction(title !== null && title !== void 0 ? title : fileName, webUrl, isRedeemed);
            },
        });
    }
    // In embemd workload, we register AddToToDo action in actions-service because there is no AppBar in embed workload.
    // For other workflows, AddToToDo is registered when loading AppBar which loads mos apps and registers mos app content actions through m365-apps-service.
    if (isEmbed &&
        featureGates.addToDoEnabled &&
        ((userType === 'Enterprise' && exchangeEnabled) || isConsumerGraphUser)) {
        var addToToDoActions = getAddToToDoActions(DefaultOfficeAppId, resourceStrings, isHwa);
        if (addToToDoActions && addToToDoActions.length) {
            actions.push.apply(actions, __spreadArray([], __read(addToToDoActions), false));
        }
    }
    // In embed workload, we register convertToPdf action in actions-service because there is no AppBar in embed workload.
    // For other workflows, convertToPdf is registered when loading AppBar which loads mos apps and registers mos app content actions through m365-apps-service.
    if (isEmbed &&
        featureGates.convertToPdfEnabled &&
        oneDriveEnabled &&
        (userType === 'Enterprise' || isConsumerGraphUser)) {
        var convertToPdfActions = getConvertToPdfActions(DefaultOfficeAppId, resourceStrings, isHwa);
        if (convertToPdfActions && convertToPdfActions.length) {
            actions.push.apply(actions, __spreadArray([], __read(convertToPdfActions), false));
        }
    }
    actionManager.registerResolvedActions(actions);
    logRegisteredActions(systemUserConfig, actions);
};
//# sourceMappingURL=registerHubActions.js.map